<template>
    <div class="flow-details__wrap">
      <div>
        <ui-radio-group class="flow-radio-gr mb-8" v-model="relation">
          <template #label>
            <div class="font-weight-bold">{{ t('Logical relation') }}
              <ui-tooltip-icon
                preset="help"
                class="mb-2 ml-1"
                :text="t('Logical relations between conditions in rule')"
              ></ui-tooltip-icon>
            </div>
          </template>
          <div class="d-flex align-baseline mt-16">
            <ui-radio value="and" :label="t('Logical AND')"></ui-radio>
            <ui-radio class="ml-16" value="or" :label="t('Logical OR')"></ui-radio>
          </div>
        </ui-radio-group>
      </div>
      <div class="action-buttons button-wrapper mb-8 pl-0 pr-0">
        <div class="action-buttons--left">
          <ui-button
            icon="fas fa-plus"
            :text="t('Add condition')"
            button-style="accent"
            :menu-list="menuList"
            :menu-props="{
              maxHeight: 'calc(100vh - 150px)',
              nudgeTop: 150,
              condenced: true
            }"
          />
          <ui-button
            icon="far fa-trash-alt"
            :text="t('Remove all conditions')"
            @click="deselectAll"
          />
        </div>

        <ui-info
          v-if="isClientHintsNoticeVisible"
          :close="true"
          name="clientHintsNoticeRulesCampaign"
          width="w-auto"
        >
          <div>
            <a href="/app/profile">
              {{ t('Activate Client Hints to get more details on OS, device, browser reports.') }}
            </a>
          </div>
        </ui-info>
      </div>

      <div class="flow-rule__card">
        <div v-if="!selected.length" class="flow-rule__empty">{{ t('Rule conditions are empty') }}</div>
        <brand-and-model
          v-if="isSelected('brand')"
          @close="remove('brand')"
          :conditions="rule.conditions"
        ></brand-and-model>
        <browser-and-version
          v-if="isSelected('browser')"
          @close="remove('browser')"
          :conditions="rule.conditions"
        ></browser-and-version>
        <connection-type
          v-if="isSelected('connectType')"
          @close="remove('connectType')"
          :conditions="rule.conditions"
        ></connection-type>
        <dictionary
          v-if="isSelected('country')"
          @close="remove('country')"
          :title="t('Country')"
          :searchPlaceholder="t('Select countries...')"
          api="countries"
          name="country"
          :allow-custom="false"
          :conditions="rule.conditions"
          :withCustomArea="true"
        ></dictionary>
        <dictionary
          v-if="isSelected('state')"
          @close="remove('state')"
          :title="t('State / region')"
          :searchPlaceholder="t('Type state / region name...')"
          api="states"
          name="state"
          :allow-custom="false"
          :conditions="rule.conditions"
          :withCustomArea="true"
        ></dictionary>
        <dictionary
          v-if="isSelected('city')"
          @close="remove('city')"
          :title="t('City')"
          :searchPlaceholder="t('Type city name...')"
          name="city"
          api="cities"
          :allow-custom="false"
          :conditions="rule.conditions"
          :withCustomArea="true"
        ></dictionary>
        <custom-token
          v-if="isSelected('customToken')"
          @close="remove('customToken')"
          :conditions="rule.conditions"
        ></custom-token>
        <external-id
          v-if="isSelected('externalId')"
          @close="remove('externalId')"
          :conditions="rule.conditions"
        ></external-id>
        <query-header
          v-if="isSelected('queryHeader')"
          @close="remove('queryHeader')"
          :conditions="rule.conditions"
        ></query-header>
        <template v-for="index in 10" :key="index">
          <custom
            v-if="isSelected('custom' + index)"
            @close="remove('custom' + index)"
            :title="t('Custom {0}', [index])"
            :name="'custom' + index"
            :conditions="rule.conditions"
          ></custom>
        </template>
        <day-of-week
          v-if="isSelected('dayOfWeek')"
          @close="remove('dayOfWeek')"
          :conditions="rule.conditions"
        ></day-of-week>
        <device-type
          v-if="isSelected('deviceType')"
          @close="remove('deviceType')"
          :conditions="rule.conditions"
        ></device-type>
        <ip
          v-if="isSelected('ip')"
          @close="remove('ip')"
          :with-custom-area="true"
          :conditions="rule.conditions"
        ></ip>
        <dictionary
          v-if="isSelected('isp')"
          @close="remove('isp')"
          :title="t('ISP')"
          :searchPlaceholder="t('Type ISP name...')"
          name="isp"
          api="isps"
          :allow-custom="false"
          :conditions="rule.conditions"
          :withCustomArea="true"
        ></dictionary>
        <dictionary
          v-if="isSelected('language')"
          @close="remove('language')"
          :title="t('Language')"
          :searchPlaceholder="t('Select languages...')"
          api="languages"
          name="language"
          :conditions="rule.conditions"
          :allow-custom="false"
          :withCustomArea="true"
        ></dictionary>
        <dictionary
          v-if="isSelected('mobileCarrier')"
          @close="remove('mobileCarrier')"
          :title="t('Mobile Carrier')"
          :searchPlaceholder="t('Type mobile carrier name...')"
          name="mobileCarrier"
          api="mobile-carriers"
          :conditions="rule.conditions"
          :withCustomArea="true"
        ></dictionary>
        <os-and-version
          v-if="isSelected('os')"
          @close="remove('os')"
          :conditions="rule.conditions"
        ></os-and-version>
        <referrerDomain
          v-if="isSelected('referrerDomain')"
          @close="remove('referrerDomain')"
          :conditions="rule.conditions"
        ></referrerDomain>
        <referrer
          v-if="isSelected('referrer')"
          @close="remove('referrer')"
          :conditions="rule.conditions"
        ></referrer>
        <time-of-day
          v-if="isSelected('timeOfDay')"
          @close="remove('timeOfDay')"
          :conditions="rule.conditions"
        ></time-of-day>
        <timezone
          v-if="isTimezoneAvailable"
          :conditions="rule.conditions"
        ></timezone>
        <user-agent
          v-if="isSelected('userAgent')"
          @close="remove('userAgent')"
          :conditions="rule.conditions"
          :with-custom-area="true"
        ></user-agent>
        <uniqueness
          v-if="isSelected('uniqueness')"
          @close="remove('uniqueness')"
          :conditions="rule.conditions"
        ></uniqueness>
        <uniq-offer-viewer
          v-if="isSelected('uniqOfferViewer')"
          @close="remove('uniqOfferViewer')"
          :conditions="rule.conditions"
        ></uniq-offer-viewer>
        <bot-filter
          v-if="isSelected('botMark')"
          @close="remove('botMark')"
          :conditions="rule.conditions"
        ></bot-filter>
        <webview
          v-if="isSelected('webViewMark')"
          @close="remove('webViewMark')"
          :conditions="rule.conditions"
        ></webview>
        <crawler
          v-if="isSelected('crawler')"
          @close="remove('crawler')"
          :conditions="rule.conditions"
        ></crawler>
        <proxy
          v-if="isSelected('proxy')"
          @close="remove('proxy')"
          :conditions="rule.conditions"
        ></proxy>
        <visit-cap
          v-if="isSelected('visitCap')"
          @close="remove('visitCap')"
          :conditions="rule.conditions"
        ></visit-cap>
        <ipBlacklisted
          v-if="isSelected('ipBlacklisted')"
          @close="remove('ipBlacklisted')"
          :conditions="rule.conditions"
        ></ipBlacklisted>
        <ipDataCenter
          v-if="isSelected('ipDataCenter')"
          @close="remove('ipDataCenter')"
          :conditions="rule.conditions"
        ></ipDataCenter>
        <ipWithFrequentEvents
          v-if="isSelected('ipWithFrequentEvents')"
          @close="remove('ipWithFrequentEvents')"
          :conditions="rule.conditions"
        ></ipWithFrequentEvents>
        <ipWithFrequentCampaignEvents
          v-if="isSelected('ipWithFrequentCampaignEvents')"
          @close="remove('ipWithFrequentCampaignEvents')"
          :conditions="rule.conditions"
        ></ipWithFrequentCampaignEvents>
      </div>
    </div>
</template>

<script setup lang="ts">
import BrandAndModel from '@/views/reports/forms/flow/views/conditions/brand-and-model.vue';
import BrowserAndVersion from '@/views/reports/forms/flow/views/conditions/browser-and-version.vue';
import OsAndVersion from '@/views/reports/forms/flow/views/conditions/os-and-version.vue';
import Dictionary from '@/views/reports/forms/flow/views/conditions/dictionary.vue';
import ConnectionType from '@/views/reports/forms/flow/views/conditions/connection-type.vue';
import Custom from '@/views/reports/forms/flow/views/conditions/custom.vue';
import CustomToken from '@/views/reports/forms/flow/views/conditions/custom-token.vue';
import QueryHeader from '@/views/reports/forms/flow/views/conditions/query-header.vue';
import DayOfWeek from '@/views/reports/forms/flow/views/conditions/day-of-week.vue';
import DeviceType from '@/views/reports/forms/flow/views/conditions/device-type.vue';
import Ip from '@/views/reports/forms/flow/views/conditions/ip.vue';
import Referrer from '@/views/reports/forms/flow/views/conditions/referrer.vue';
import ReferrerDomain from '@/views/reports/forms/flow/views/conditions/referrer-domain.vue';
import UserAgent from '@/views/reports/forms/flow/views/conditions/user-agent.vue';
import TimeOfDay from '@/views/reports/forms/flow/views/conditions/time-of-day.vue';
import Timezone from '@/views/reports/forms/flow/views/conditions/timezone.vue';
import Uniqueness from '@/views/reports/forms/flow/views/conditions/uniqueness.vue';
import BotFilter from '@/views/reports/forms/flow/views/conditions/bot-filter.vue';
import Webview from '@/views/reports/forms/flow/views/conditions/webview.vue';
import Crawler from '@/views/reports/forms/flow/views/conditions/crawler.vue';
import Proxy from '@/views/reports/forms/flow/views/conditions/proxy.vue';
import VisitCap from '@/views/reports/forms/flow/views/conditions/visit-cap.vue';
import IpBlacklisted from '@/views/reports/forms/flow/views/conditions/ip-blacklisted.vue';
import IpDataCenter from '@/views/reports/forms/flow/views/conditions/ip-data-center.vue';
import IpWithFrequentEvents from '@/views/reports/forms/flow/views/conditions/ip-with-frequent-events.vue';
import IpWithFrequentCampaignEvents from '@/views/reports/forms/flow/views/conditions/ip-with-frequent-campaign-events.vue';
import UniqOfferViewer from '@/views/reports/forms/flow/views/conditions/uniq-offer-viewer.vue';
import ExternalId from '@/views/reports/forms/flow/views/conditions/external-id.vue';
import { useCampaignRuleSettings } from './useCampaignRuleSettings';

const relation = defineModel<string>('relation', { default: 'and' });

const props = defineProps<{
  target: unknown;
  rule: {
    conditions: Record<string, unknown>;
    status?: string;
  };
  active_name: string;
}>();

const {
  t,
  menuList,
  isTimezoneAvailable,
  isClientHintsNoticeVisible,
  isSelected,
  remove,
  deselectAll,
  selected,
} = useCampaignRuleSettings(props);
</script>

<style lang="scss" scoped>
.button-wrapper {
  display: flex;
}

.action-buttons{
  display: flex;
  justify-content:space-between;
  flex-wrap: wrap;
  gap:6px;
}

.action-buttons--left{
  display: flex;
  flex-wrap: nowrap;
}
</style>
